/**
 * Desk generator — a work desk built from box geometry.
 *
 * Coordinate convention: Z-up, 1 unit = 1 metre.
 *   - X = width (left–right)
 *   - Y = depth (front–back; user faces +Y)
 *   - Z = height
 *
 * Local anchor: origin at the geometric centre of the footprint at Z = 0.
 *
 * Default dimensions match a standard office desk: 1.2 m wide × 0.6 m deep
 * × 0.74 m high — the same height as the table generator so desk and chair
 * generators pair correctly out of the box.
 *
 * Leg styles:
 *   four_leg  — four corner legs, inset from the top edge.
 *   panel     — two full-height side panels (like a panel-leg desk).
 *   trestle   — two T-shaped trestle supports, one near each end.
 *
 * Optional modesty panel: a vertical back-panel that hangs below the desk
 * surface at the rear, providing a legible desk silhouette for sitting poses.
 * Optional under-desk drawer box: a shallow rectangular volume under the
 * desk top, centred, for context in work-pose reference.
 */

import type { BoxSpec } from "./generatorGeom.ts";
import { concatBoxes } from "./generatorGeom.ts";
import type {
  Generator,
  GeneratorBuildContext,
  GeneratorOutput,
  GeneratorParamSpec,
  GeneratorParams,
} from "./types.ts";
import {
  boolParam,
  defineGenerator,
  enumParam,
  numParam,
  paramsFromSpecs,
} from "./types.ts";

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

interface DeskParams extends GeneratorParams {
  width: number;
  depth: number;
  height: number;
  topThickness: number;
  legStyle: string;
  legSize: number;
  legInset: number;
  hasModestyPanel: boolean;
  modestPanelHeight: number;
  hasDrawerBox: boolean;
}

const DESK_SPECS: readonly GeneratorParamSpec[] = [
  {
    type: "section",
    id: "desk-top",
    label: "Top",
    params: [
      {
        id: "width",
        label: "Width",
        type: "number",
        unit: "meters",
        default: 1.2,
        min: 0.6,
        max: 3.0,
        step: 0.05,
      },
      {
        id: "depth",
        label: "Depth",
        type: "number",
        unit: "meters",
        default: 0.6,
        min: 0.4,
        max: 1.2,
        step: 0.025,
      },
      {
        id: "height",
        label: "Height",
        type: "number",
        unit: "meters",
        default: 0.74,
        min: 0.5,
        max: 1.0,
        step: 0.01,
      },
      {
        id: "topThickness",
        label: "Top Thickness",
        type: "number",
        unit: "meters",
        default: 0.03,
        min: 0.01,
        max: 0.08,
        step: 0.005,
      },
    ],
  },
  {
    type: "section",
    id: "desk-legs",
    label: "Legs",
    params: [
      {
        id: "legStyle",
        label: "Style",
        type: "enum",
        default: "four_leg",
        options: [
          { value: "four_leg", label: "Four Legs" },
          { value: "panel", label: "Side Panels" },
          { value: "trestle", label: "Trestle" },
        ],
      },
      {
        id: "legSize",
        label: "Leg Size",
        type: "number",
        unit: "meters",
        default: 0.05,
        min: 0.02,
        max: 0.12,
        step: 0.005,
        showIf: { id: "legStyle", value: "four_leg" },
      },
      {
        id: "legInset",
        label: "Inset",
        type: "number",
        unit: "meters",
        default: 0.02,
        min: 0.0,
        max: 0.15,
        step: 0.005,
        showIf: { id: "legStyle", value: "four_leg" },
      },
    ],
  },
  {
    type: "section",
    id: "desk-extras",
    label: "Details",
    defaultCollapsed: true,
    params: [
      {
        id: "hasModestyPanel",
        label: "Modesty Panel",
        type: "boolean",
        default: false,
      },
      {
        id: "modestPanelHeight",
        label: "Modesty Panel Height",
        type: "number",
        unit: "meters",
        default: 0.3,
        min: 0.1,
        max: 0.6,
        step: 0.025,
        showIf: { id: "hasModestyPanel", value: true },
      },
      {
        id: "hasDrawerBox",
        label: "Drawer Box",
        type: "boolean",
        default: false,
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

function buildDesk(
  p: DeskParams,
  _ctx: GeneratorBuildContext,
): GeneratorOutput {
  const width = numParam(p, "width", 1.2);
  const depth = numParam(p, "depth", 0.6);
  const height = numParam(p, "height", 0.74);
  const topT = numParam(p, "topThickness", 0.03);
  const legStyle = enumParam(p, "legStyle", "four_leg");
  const legSize = numParam(p, "legSize", 0.05);
  const legInset = numParam(p, "legInset", 0.02);
  const hasModesty = boolParam(p, "hasModestyPanel", false);
  const modestyH = numParam(p, "modestPanelHeight", 0.3);
  const hasDrawerBox = boolParam(p, "hasDrawerBox", false);

  const boxes: BoxSpec[] = [];

  // ── Top surface ───────────────────────────────────────────────────────────
  // Top face at Z = height.
  boxes.push({
    cx: 0,
    cy: 0,
    cz: height - topT * 0.5,
    sx: width,
    sy: depth,
    sz: topT,
  });

  // ── Leg structure ─────────────────────────────────────────────────────────
  const legTopZ = height - topT;
  const legH = legTopZ;
  const legCZ = legH * 0.5;

  if (legStyle === "four_leg") {
    const xOff = width * 0.5 - legInset - legSize * 0.5;
    const yOff = depth * 0.5 - legInset - legSize * 0.5;
    for (const sx of [1, -1]) {
      for (const sy of [1, -1]) {
        boxes.push({
          cx: sx * xOff,
          cy: sy * yOff,
          cz: legCZ,
          sx: legSize,
          sy: legSize,
          sz: legH,
        });
      }
    }
  } else if (legStyle === "panel") {
    // Two side panels spanning the full depth of the desk, flush with the
    // left and right edges of the top.
    const panelT = 0.02;
    boxes.push({
      cx: width * 0.5 - panelT * 0.5,
      cy: 0,
      cz: legCZ,
      sx: panelT,
      sy: depth,
      sz: legH,
    });
    boxes.push({
      cx: -width * 0.5 + panelT * 0.5,
      cy: 0,
      cz: legCZ,
      sx: panelT,
      sy: depth,
      sz: legH,
    });
  } else {
    // Trestle: two inverted-T supports, each set in from the ends.
    // Vertical post + horizontal foot spanning the full depth.
    const trestleT = 0.04;
    const footT = 0.03;
    const trestleXOff = width * 0.5 - trestleT * 2;
    for (const sx of [1, -1]) {
      // Vertical post
      boxes.push({
        cx: sx * trestleXOff,
        cy: 0,
        cz: legCZ,
        sx: trestleT,
        sy: depth * 0.6,
        sz: legH,
      });
      // Horizontal foot plate at floor level
      boxes.push({
        cx: sx * trestleXOff,
        cy: 0,
        cz: footT * 0.5,
        sx: trestleT * 1.5,
        sy: depth * 0.7,
        sz: footT,
      });
    }
    // Stretcher connecting the two trestles near floor level
    boxes.push({
      cx: 0,
      cy: 0,
      cz: trestleT * 0.5,
      sx: Math.max(0.01, width - trestleT * 4),
      sy: trestleT,
      sz: trestleT,
    });
  }

  // ── Optional drawer box ───────────────────────────────────────────────────
  // A shallow box hung under the top surface, centred in X and Y.
  if (hasDrawerBox) {
    const boxW = Math.min(width * 0.5, 0.5);
    const boxD = depth * 0.6;
    const boxH = 0.1;
    const boxT = 0.018;
    const boxBottomZ = height - topT - boxH;
    // Bottom
    boxes.push({
      cx: 0,
      cy: 0,
      cz: boxBottomZ + boxT * 0.5,
      sx: boxW,
      sy: boxD,
      sz: boxT,
    });
    // Front face (facing user)
    boxes.push({
      cx: 0,
      cy: depth * 0.5 - boxT * 0.5,
      cz: boxBottomZ + boxH * 0.5,
      sx: boxW,
      sy: boxT,
      sz: boxH,
    });
    // Left / right
    boxes.push({
      cx: boxW * 0.5 - boxT * 0.5,
      cy: 0,
      cz: boxBottomZ + boxH * 0.5,
      sx: boxT,
      sy: boxD,
      sz: boxH,
    });
    boxes.push({
      cx: -boxW * 0.5 + boxT * 0.5,
      cy: 0,
      cz: boxBottomZ + boxH * 0.5,
      sx: boxT,
      sy: boxD,
      sz: boxH,
    });
  }

  // ── Optional modesty panel ────────────────────────────────────────────────
  // A vertical back panel hanging from the underside of the top at the rear.
  if (hasModesty) {
    const panelT = 0.018;
    const clampedH = Math.min(modestyH, height - topT - 0.02);
    const panelCZ = height - topT - clampedH * 0.5;
    boxes.push({
      cx: 0,
      cy: -(depth * 0.5 - panelT * 0.5),
      cz: panelCZ,
      sx: Math.max(0.01, width),
      sy: panelT,
      sz: clampedH,
    });
  }

  return { kind: "mesh", geometry: concatBoxes(boxes) };
}

// ---------------------------------------------------------------------------
// Generator definition
// ---------------------------------------------------------------------------

export const DESK_GENERATOR: Generator = defineGenerator<DeskParams>({
  id: "desk",
  label: "Desk",
  category: "prop",
  paramSpecs: DESK_SPECS,
  defaultParams: paramsFromSpecs(DESK_SPECS) as DeskParams,
  build: buildDesk,
});
